/**
 * Sablonok engedélyezése a dolgozónak (admin oldal).
 *
 * Lekéri az összes shift template-et és checkbox-listán választható
 * mely sablonokat választhatja a dolgozó tervezet leadáskor.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Check, ChevronDown, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function AllowedTemplatesSection({ employee }) {
  const [allTemplates, setAllTemplates] = useState([]);
  const [allowedIds, setAllowedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (!employee?._id) return;
    setLoading(true);
    try {
      const [tplRes, allowedRes] = await Promise.all([
        api.get("/shift-templates"),
        api.get(`/employees/${employee._id}/allowed-templates`),
      ]);
      setAllTemplates(tplRes.data || []);
      const ids = new Set(allowedRes.data?.template_ids || []);
      setAllowedIds(ids);
      setOriginalIds(new Set(ids));
    } catch (e) {
      toast.error(formatApiError(e) || "Betöltési hiba.");
    } finally {
      setLoading(false);
    }
  }, [employee?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => {
    if (allowedIds.size !== originalIds.size) return true;
    for (const id of allowedIds) {
      if (!originalIds.has(id)) return true;
    }
    return false;
  }, [allowedIds, originalIds]);

  // Group templates by site
  const grouped = useMemo(() => {
    const map = new Map();
    allTemplates.forEach((t) => {
      const key = t.site_id || "—";
      const siteName = t.site_name || "—";
      if (!map.has(key)) map.set(key, { site_name: siteName, items: [] });
      map.get(key).items.push(t);
    });
    // Sort: employee's own site first
    const arr = Array.from(map.entries());
    arr.sort(([a], [b]) => {
      if (employee?.site_id) {
        if (a === employee.site_id) return -1;
        if (b === employee.site_id) return 1;
      }
      return 0;
    });
    return arr;
  }, [allTemplates, employee?.site_id]);

  const toggle = (id) => {
    setAllowedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${employee._id}/allowed-templates`, {
        template_ids: Array.from(allowedIds),
      });
      setOriginalIds(new Set(allowedIds));
      toast.success("Engedélyezett sablonok mentve.");
    } catch (e) {
      toast.error(formatApiError(e) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setAllowedIds(new Set(originalIds));

  return (
    <section
      data-testid="allowed-templates-section"
      className="rounded-2xl border border-border bg-card p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="inline-flex items-center gap-2 text-[14px] font-semibold tracking-tight">
            <Layers className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            Tervezethez engedélyezett sablonok
          </h3>
          <p className="mt-1 max-w-md text-[12px] text-muted-foreground">
            A dolgozó a portálon leadhat &bdquo;tervezet&rdquo; típusú beosztás-javaslatot.
            Itt szabhatod meg, mely sablonok közül választhat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              className="h-8"
              data-testid="allowed-templates-reset"
            >
              Visszavon
            </Button>
          )}
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-8"
            data-testid="allowed-templates-save"
          >
            {saving ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="mr-1 h-3.5 w-3.5" strokeWidth={2.5} />
            )}
            Mentés
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="grid place-items-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : allTemplates.length === 0 ? (
          <p className="rounded-xl bg-muted/40 py-6 text-center text-[12.5px] text-muted-foreground">
            Még nincs sablon a rendszerben. Hozz létre sablonokat a Beosztás oldalon.
          </p>
        ) : (
          grouped.map(([siteId, { site_name, items }], idx) => {
            const isOwn = employee?.site_id === siteId;
            const hidden = !showAll && !isOwn && idx > 0;
            if (hidden) return null;
            return (
              <div key={siteId}>
                <p className="mb-1.5 inline-flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {site_name}
                  {isOwn && (
                    <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                      Saját telephely
                    </span>
                  )}
                </p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {items.map((t) => {
                    const id = t._id || t.id;
                    const checked = allowedIds.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggle(id)}
                        data-testid={`allow-tpl-${id}`}
                        className={
                          "flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors " +
                          (checked
                            ? "border-blue-500 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/30"
                            : "border-border bg-background hover:bg-muted/40")
                        }
                      >
                        <span
                          className={
                            "grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors " +
                            (checked
                              ? "border-blue-500 bg-blue-500 text-white"
                              : "border-border bg-card")
                          }
                        >
                          {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <p className="truncate text-[12.5px] font-semibold tracking-tight">
                            {t.name}
                          </p>
                          <p className="font-mono text-[11px] text-muted-foreground">
                            {t.start_time}–{t.end_time}
                          </p>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
        {!showAll && grouped.length > 1 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            data-testid="allowed-templates-show-all"
            className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            További telephelyek sablonjai
            <ChevronDown className="h-3 w-3" strokeWidth={2} />
          </button>
        )}
      </div>
    </section>
  );
}
